import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import AuthLayout from '@/components/layouts/AuthLayout';

const loginSchema = z.object({
  email: z.string().email('Bitte geben Sie eine gültige E-Mail-Adresse ein'),
  password: z.string().min(6, 'Das Passwort muss mindestens 6 Zeichen lang sein'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { loginMutation } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Don't force reset form on mount - this clears error state
  // React.useEffect(() => {
  //   reset({
  //     email: '',
  //     password: '',
  //   });
  // }, [reset]);

  const onSubmit = async (data: LoginFormData) => {
    setLocalError(null);

    try {
      await loginMutation.mutateAsync(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Fehler beim Anmelden. Bitte überprüfen Sie Ihre Anmeldedaten.';
      setLocalError(errorMessage);
    }
  };

  return (
    <AuthLayout
              title="Willkommen bei Sky View"
      subtitle="Melden Sie sich in Ihr Konto an"
      showBackButton={true}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit)(e);
        }}
        className="space-y-6"
        autoComplete="off"
      >
        {/* Email */}
        <div>
          <label htmlFor="email" className="label">
            E-Mail-Adresse
          </label>
          <input
            {...register('email')}
            type="email"
            id="email"
            className="input mt-1"
            placeholder="ihre.email@skyview-assets.de"
            autoComplete="off"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="label">
            Passwort
          </label>
          <div className="relative mt-1">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              className="input pr-10"
              placeholder="••••••••"
              autoComplete="off"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-slate-400" />
              ) : (
                <Eye className="h-4 w-4 text-slate-400" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-800">
              Angemeldet bleiben
            </label>
          </div>



        {/* Submit button */}
        <Button
          type="submit"
          disabled={isSubmitting || loginMutation.isPending}
          className="w-full bg-primary-900 hover:bg-primary-950 text-white py-3 font-medium border border-primary-800"
        >
          {(isSubmitting || loginMutation.isPending) ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Wird angemeldet...
            </>
          ) : (
            'Anmelden'
          )}
        </Button>


      </form>
    </AuthLayout>
  );
};

export default LoginPage;
